"use client";

import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
export default function Home() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_zvt30j09.jpg", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_oxbyize4.jpg", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_8rncz6zx.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_6w1r0k6h.jpg", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_qbdt4zrk.jpg", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_shcw1nao.png"];
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const calculateTimeLeft = () => {
      const weddingDate = new Date("2026-07-11T10:30:00").getTime();
      const now = new Date().getTime();
      const difference = weddingDate - now;
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference / (1000 * 60 * 60) % 24),
          minutes: Math.floor(difference / 1000 / 60 % 60),
          seconds: Math.floor(difference / 1000 % 60)
        });
      } else {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 2000);
    return () => clearInterval(timer);
  }, [slides.length]);
  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Vivaldi&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };
  const goToPrevious = () => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  };
  const goToNext = () => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
  };
  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  return <section ref={ref} className="relative h-screen w-full overflow-hidden bg-accent">
      {}
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_iuotf5pz.png)]" />

      {}
      <div className="absolute inset-0 overflow-hidden">
        <div className="flex h-full transition-transform duration-[var(--duration-slow)] ease-[var(--ease-smooth)]" style={{
        transform: `translateX(-${currentSlide * 100}%)`
      }}>
          {slides.map((slide, index) => <div key={slide} data-index={index} className="min-w-full h-full bg-cover bg-center" style={{
          backgroundImage: `url(${slide})`
        }} />)}
        </div>
      </div>

      {}
      <div className="absolute top-0 bottom-0 -left-[100vw] -right-[100vw] w-[300vw] bg-gradient-to-b from-accent/40 via-accent/40 to-accent/40" />

      {}
      <Button onClick={goToPrevious} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary shadow-xs h-9 has-[>svg]:px-3 absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full p-3 text-foreground shadow-medium backdrop-blur-sm transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring !bg-[hsl(24,6%,83%)] !opacity-[0.2] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary-light hover:scale-110 md:left-8 md:p-4" aria-label="Previous slide">
        <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
      </Button>

      {}
      <Button onClick={goToNext} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary shadow-xs h-9 has-[>svg]:px-3 absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full p-3 text-foreground shadow-medium backdrop-blur-sm transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring !bg-[hsl(24,6%,83%)] !opacity-[0.2] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary-light hover:scale-110 md:right-8 md:p-4" aria-label="Next slide">
        <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
      </Button>

      {}
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-4">
        {}
        <div className={`text-center transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-2.5 opacity-0"}`}>
          <h1 className="mb-6 font-display italic tracking-wider drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)] flex flex-col items-center gap-2 !text-9xl !text-[hsl(221,39%,11%)] !font-extralight" style={{
          fontFamily: "'Alex Brush', cursive"
        }}>
            <span style={{
            fontFamily: "'Vivaldi', cursive"
          }} className="text-[30px] !font-light md:text-[96px]">
              Vanessa Dwairi
            </span>
            <span className="text-2xl !font-serif !font-medium md:text-5xl">
              &
            </span>
            <span style={{
            fontFamily: "'Vivaldi', cursive"
          }} className="text-[30px] !font-light md:text-[96px]">
              Brian Abi Nader
            </span>
          </h1>

          {}
          <div className="mt-8 mb-8 mx-auto max-w-2xl opacity-100">
            <div className="grid grid-cols-4 gap-6 md:gap-10">
              <div className="flex flex-col items-center justify-center !text-[hsl(221,39%,11%)]">
                <div className="text-3xl font-display font-bold px-3 py-2 rounded-md !text-[hsl(221,39%,11%)] md:text-5xl" style={{
                fontFamily: "Mereoleona"
              }}>
                  {timeLeft.days}
                </div>
                <div className="text-[10px] font-sans uppercase tracking-wider mt-2 px-2 py-1 rounded md:text-xs">
                  Days
                </div>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="text-3xl font-display font-bold px-3 py-2 rounded-md !text-[hsl(221,39%,11%)] md:text-5xl" style={{
                fontFamily: "Mereoleona"
              }}>
                  {timeLeft.hours}
                </div>
                <div className="text-[10px] font-sans uppercase tracking-wider mt-2 px-2 py-1 rounded !text-[hsl(221,39%,11%)] md:text-xs">
                  Hours
                </div>
              </div>

              <div className="flex flex-col items-center justify-center !text-[hsl(221,39%,11%)]">
                <div className="text-3xl font-display font-bold px-3 py-2 rounded-md !text-[hsl(221,39%,11%)] md:text-5xl" style={{
                fontFamily: "Mereoleona"
              }}>
                  {timeLeft.minutes}
                </div>
                <div className="text-[10px] font-sans uppercase tracking-wider mt-2 px-2 py-1 rounded !text-[hsl(221,39%,11%)] md:text-xs">
                  Minutes
                </div>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="text-3xl font-display font-bold px-3 py-2 rounded-md !text-[hsl(221,39%,11%)] md:text-5xl" style={{
                fontFamily: "Mereoleona"
              }}>
                  {timeLeft.seconds}
                </div>
                <div className="text-[10px] font-sans uppercase tracking-wider mt-2 px-2 py-1 rounded !text-[hsl(221,39%,11%)] md:text-xs">
                  Seconds
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="mt-6 flex justify-center">
            <Button onClick={toggleAudio} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive shadow-xs hover:bg-primary/90 h-9 has-[>svg]:px-3 rounded-full p-4 bg-primary-dark text-white shadow-medium backdrop-blur-sm transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-110 hover:shadow-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={isPlaying ? "Pause music" : "Play music"}>
              {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </Button>
          </div>

          {}
          <audio ref={audioRef} loop preload="auto">
            <source src="https://vanessabrian.com/wp-content/uploads/2026/01/Frank-Sinatra-L.O.V.E.-lyrics-1.mp3" type="audio/mpeg" />
          </audio>
        </div>
      </div>

      {}
      <div className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 gap-2">
        {slides.map((_, index) => <Button key={index} data-index={index} onClick={() => goToSlide(index)} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring aria-invalid:ring-destructive/20 aria-invalid:border-destructive text-primary-foreground shadow-xs has-[>svg]:px-3 h-2.5 rounded-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring p-0 w-8 bg-primary shadow-soft !opacity-[0.2] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90" aria-label={`Go to slide ${index + 1}`} />)}
      </div>
    </section>;
}